import CustomLoadingButton from 'Components/CustomLoadingButton'
import FormTextField from 'Components/FormTextField'
import StyledForm from 'Components/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import { SELECTED_REFUND_CODE_PARAMETER_KEY } from 'Services/ControlDataManagement/Features/RefundCode/Hooks/useRefundCodesWithParams'
import {
  useCreateSingleRefundCodeMutation,
  useUpdateSingleRefundCodeMutation
} from 'Services/ControlDataManagement/Features/RefundCode/refundCodeApi'
import type { RefundCode } from 'Services/ControlDataManagement/Features/RefundCode/types'
import type { FieldValidationErrors } from 'Services/ControlDataManagement/types'
import type { ControlDataServiceError } from 'Util/ApiClient'
import { useValidationErrors } from 'Util/hooks/useValidationErrors'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { Box, Grid } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: FieldValidationErrors = {
  refund_code_id: [],
  explanation: []
}

interface RefundCodeFormProps {
  successBaseUrl: string
  submitLoading: boolean
  refundCode?: RefundCode
}

export default function RefundCodeForm({
  refundCode,
  successBaseUrl,
  submitLoading
}: Readonly<RefundCodeFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!refundCode

  const { setValidationErrors, getErrorProps } = useValidationErrors(fieldErrorsDefaultState)

  const [createSingleRefundCode, { isLoading: createLoading }] = useCreateSingleRefundCodeMutation()
  const [updateSingleRefundCode, { isLoading: updateLoading }] = useUpdateSingleRefundCodeMutation()
  const [refundCodeDetails, setRefundCodeDetails] = useState<Partial<RefundCode>>(
    (isEdit && refundCode) || {}
  )

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof RefundCode
  ) => {
    if (value !== undefined) {
      setRefundCodeDetails(prevState => ({ ...prevState, [field]: value }) as RefundCode)
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (refundCode) {
      updateSingleRefundCode({
        refund_code_id: refundCode.refund_code_id,
        ...refundCodeDetails
      })
        .unwrap()
        .then(() => {
          SnackbarUtils.success(t`Refund code updated successfully.`)
          navigate(successBaseUrl)
        })
        .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
          const { violations } = errorResponse
          setValidationErrors(violations)
          SnackbarUtils.error(t`There was an error updating the refund code.`)
        })
      return
    }

    createSingleRefundCode(refundCodeDetails)
      .unwrap()
      .then((refundCodeResponse: RefundCode) => {
        SnackbarUtils.success(t`Refund code created successfully.`)
        navigate(
          appendQueryParamsToUrl(successBaseUrl, {
            [SELECTED_REFUND_CODE_PARAMETER_KEY]: refundCodeResponse.refund_code_id
          })
        )
      })
      .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
        const { violations } = errorResponse
        setValidationErrors(violations)
        SnackbarUtils.error(t`There was an error creating the refund code.`)
      })
  }

  const isSubmitLoading = submitLoading || createLoading || updateLoading

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
          <Box>
            <FormTextField
              name="refund_code_id"
              required
              label={t`Refund code identifier`}
              value={refundCodeDetails?.refund_code_id ?? ''}
              maxLength={40}
              onChange={e => handleChange(e.target.value, 'refund_code_id')}
              {...getErrorProps('refund_code_id')}
            />
          </Box>
          <StyledLabel>{t`Translations`}</StyledLabel>
          <Box>
            <FormTextField
              name="explanation"
              required
              label={t`Explanation`}
              value={refundCodeDetails?.explanation ?? ''}
              maxLength={500}
              onChange={e => handleChange(e.target.value, 'explanation')}
              multiline
              {...getErrorProps('explanation')}
            />
          </Box>
          <CustomLoadingButton
            isLoading={isSubmitLoading}
            type="submit"
            size="large"
            iconPosition="end"
          >
            {t`Save refund code`}
          </CustomLoadingButton>
        </StyledForm>
      </Grid>
    </Grid>
  )
}
